/**
 * AI CONTEXT: performance - Environment-aware asset URL helpers
 * Journal: J-20250809-01
 * 
 * Purpose:
 * - Resolve asset URLs correctly in development and production environments
 * - Provide basic existence checks and fallbacks for asset loading
 * - Centralize static asset path logic for consistency
 * - Handle environment-specific asset serving (dev vs production)
 * 
 * Architecture decisions:
 * - Environment-aware URL resolution for different deployment contexts
 * - Centralized asset path logic to prevent inconsistencies
 * - Graceful fallback handling for missing assets
 * - Support for both relative and absolute asset paths
 * 
 * Performance considerations:
 * - Efficient asset existence checking with HEAD requests
 * - Minimal overhead for asset URL resolution
 * - Support for CDN integration in production
 * 
 * Future considerations:
 * - Add CDN base URL and cache-busting for production optimization
 * - Implement smarter fallback probing with multiple fallback paths
 * - Add lazy-loading helpers for image optimization
 * - Support for WebP and other modern image formats
 * - Add asset preloading for critical resources
 */

/**
 * Utility functions for handling asset URLs in different environments
 */

/**
 * AI CONTEXT: performance - Environment-aware asset URL resolution
 * Reasoning: Provide correct asset URLs for different deployment environments
 * Constraints: Must handle both development and production asset serving
 * Future: Consider adding CDN support and cache-busting parameters
 */
export const getAssetUrl = (assetPath) => {
  // Remove leading slash if present
  const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  
  // In development, use the public URL
  if (process.env.NODE_ENV === 'development') {
    return `/${cleanPath}`;
  }
  
  // In production, use the homepage setting from package.json
  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}/${cleanPath}`;
};

/**
 * Get the correct path for static assets
 * @param {string} assetPath - The path to the asset
 * @returns {string} - The full path to the asset
 */
export const getStaticAssetPath = (assetPath) => {
  // Remove leading slash if present
  const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  
  // Use the public URL environment variable
  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}/${cleanPath}`;
};

/**
 * AI CONTEXT: performance - Asset existence checking
 * Reasoning: Verify asset availability before attempting to load for better UX
 * Constraints: Must handle network errors gracefully and provide fallbacks
 * Future: Consider implementing asset caching and preloading strategies
 */
export const checkAssetExists = async (assetPath) => {
  try {
    const response = await fetch(assetPath, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.warn(`Asset not found: ${assetPath}`, error);
    return false;
  }
};

/**
 * Get a fallback asset URL if the primary asset fails to load
 * @param {string} primaryPath - The primary asset path
 * @param {string} fallbackPath - The fallback asset path
 * @returns {string} - The asset URL to use
 */
export const getAssetWithFallback = (primaryPath, fallbackPath) => {
  const primaryUrl = getAssetUrl(primaryPath);
  
  // For now, return the primary URL
  // In a more sophisticated implementation, you could check if the asset exists
  return primaryUrl;
}; 